/**
 * VoiceRecorder
 *
 * Gravação de áudio via MediaRecorder API (Web Audio).
 * - Permissão de microfone via getUserMedia
 * - Visualização: contador de tempo + barras de onda
 * - Limite: 5 minutos por gravação (Whisper aceita até 25 MB)
 * - Geolocalização opcional (se disponível) — anexa lat/lng à nota
 *
 * No Capacitor mobile, MediaRecorder funciona pelo WebView.
 */

import { useEffect, useRef, useState } from 'react';
import { Mic, Square, Trash2, Loader2, Send } from 'lucide-react';
import { useCreateVoiceNote } from '../../hooks/useVoiceNotes';
import { toast } from 'sonner';

const MAX_DURATION_SEC = 5 * 60;  // 5 minutos

interface VoiceRecorderProps {
  contactId: string;
  onSaved?:  () => void;
}

export function VoiceRecorder({ contactId, onSaved }: VoiceRecorderProps) {
  const [isRecording, setIsRecording] = useState(false);
  const [duration,    setDuration]    = useState(0);     // segundos
  const [audioBlob,   setAudioBlob]   = useState<Blob | null>(null);
  const [audioUrl,    setAudioUrl]    = useState<string | null>(null);
  const [level,       setLevel]       = useState(0);     // 0..1, volume atual
  const [permissionErr, setPermissionErr] = useState<string | null>(null);

  const mediaRecRef     = useRef<MediaRecorder | null>(null);
  const audioCtxRef     = useRef<AudioContext | null>(null);
  const analyserRef     = useRef<AnalyserNode | null>(null);
  const streamRef       = useRef<MediaStream | null>(null);
  const startTimeRef    = useRef<number>(0);
  const timerRef        = useRef<ReturnType<typeof setInterval> | null>(null);
  const levelRafRef     = useRef<number | null>(null);
  const chunksRef       = useRef<Blob[]>([]);

  const create = useCreateVoiceNote();

  // ── Cleanup ────────────────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      stopAll();
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function stopAll() {
    if (timerRef.current) clearInterval(timerRef.current);
    if (levelRafRef.current) cancelAnimationFrame(levelRafRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    audioCtxRef.current?.close().catch(() => {});
    streamRef.current = null;
    audioCtxRef.current = null;
    analyserRef.current = null;
  }

  // ── Iniciar gravação ───────────────────────────────────────────────────────
  async function handleStart() {
    setPermissionErr(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // MediaRecorder com codec preferido
      const mimeType = preferredMimeType();
      const rec = new MediaRecorder(stream, { mimeType });
      mediaRecRef.current = rec;
      chunksRef.current = [];

      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stopAll();
      };
      rec.start();

      // Analyser para visualização de volume
      const audioCtx = new AudioContext();
      audioCtxRef.current = audioCtx;
      const source   = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;

      const buf = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) {
          const v = (buf[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / buf.length);
        setLevel(Math.min(1, rms * 4)); // amplifica visualmente
        levelRafRef.current = requestAnimationFrame(tick);
      };
      tick();

      // Timer
      startTimeRef.current = Date.now();
      setDuration(0);
      timerRef.current = setInterval(() => {
        const d = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setDuration(d);
        if (d >= MAX_DURATION_SEC) handleStop();
      }, 200);

      setIsRecording(true);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Erro ao acessar microfone';
      setPermissionErr(msg);
      toast.error('Microfone bloqueado', { description: msg });
    }
  }

  // ── Parar gravação ─────────────────────────────────────────────────────────
  function handleStop() {
    if (mediaRecRef.current && mediaRecRef.current.state !== 'inactive') {
      mediaRecRef.current.stop();
    }
    if (timerRef.current) clearInterval(timerRef.current);
    setIsRecording(false);
  }

  // ── Descartar ──────────────────────────────────────────────────────────────
  function handleDiscard() {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioBlob(null);
    setAudioUrl(null);
    setDuration(0);
  }

  // ── Salvar ─────────────────────────────────────────────────────────────────
  async function handleSave() {
    if (!audioBlob) return;

    // Tenta capturar localização (best effort)
    let coords: { latitude: number; longitude: number } | null = null;
    if ('geolocation' in navigator) {
      coords = await new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
          (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
          () => resolve(null),
          { timeout: 3000, maximumAge: 30_000 },
        );
      });
    }

    create.mutate(
      {
        contactId,
        audioBlob,
        durationSeconds: duration,
        mimeType:        audioBlob.type,
        latitude:        coords?.latitude  ?? null,
        longitude:       coords?.longitude ?? null,
      },
      {
        onSuccess: () => {
          toast.success('Nota de voz salva', {
            description: 'Transcrição em processamento…',
          });
          handleDiscard();
          onSaved?.();
        },
        onError: (e) => {
          toast.error('Falha ao salvar nota', {
            description: e instanceof Error ? e.message : 'Erro desconhecido',
          });
        },
      },
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  const isReady = !!audioBlob && !isRecording;

  return (
    <div className="rounded-2xl card-premium p-4 space-y-4">
      <div className="flex items-center gap-3">
        {!isRecording && !isReady && (
          <button
            type="button"
            onClick={handleStart}
            className="flex items-center gap-2 rounded-xl btn-accent px-4 py-2.5 text-sm font-semibold cursor-pointer"
          >
            <Mic className="h-4 w-4" />
            Gravar nota de voz
          </button>
        )}

        {isRecording && (
          <button
            type="button"
            onClick={handleStop}
            className="flex items-center gap-2 rounded-xl bg-danger/15 border border-danger/30 px-4 py-2.5 text-sm font-semibold text-danger hover:bg-danger/20 transition-colors cursor-pointer"
          >
            <Square className="h-4 w-4 fill-current" />
            Parar
          </button>
        )}

        {/* Indicador visual quando gravando */}
        {isRecording && (
          <div className="flex-grow flex items-center gap-2">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-danger opacity-75" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-danger" />
            </span>
            <span className="font-mono text-sm text-ink tabular-nums">
              {formatDuration(duration)}
            </span>
            <span className="text-[10px] text-ink-subtle">/ {formatDuration(MAX_DURATION_SEC)}</span>
            <WaveLevel level={level} />
          </div>
        )}

        {/* Preview do áudio gravado */}
        {isReady && audioUrl && (
          <div className="flex-grow flex items-center gap-3">
            <audio controls src={audioUrl} className="flex-grow max-w-md" />
            <span className="font-mono text-xs text-ink-muted">
              {formatDuration(duration)}
            </span>
          </div>
        )}
      </div>

      {/* Permission error */}
      {permissionErr && !isRecording && (
        <p className="text-[11px] text-danger">
          {permissionErr}
        </p>
      )}

      {/* Ações após gravar */}
      {isReady && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleSave}
            disabled={create.isPending}
            className="flex items-center gap-2 rounded-xl btn-accent px-4 py-2 text-sm font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-default"
          >
            {create.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Salvar e transcrever
          </button>
          <button
            type="button"
            onClick={handleDiscard}
            disabled={create.isPending}
            className="flex items-center gap-2 rounded-xl btn-ghost px-4 py-2 text-sm font-medium cursor-pointer disabled:opacity-50"
          >
            <Trash2 className="h-4 w-4" />
            Descartar
          </button>
        </div>
      )}

      <p className="text-[10px] text-ink-faint">
        Áudio enviado ao OpenAI Whisper para transcrição automática (PT-BR). Limite: 5 min por gravação.
      </p>
    </div>
  );
}

// ── Barra de nível de áudio ──────────────────────────────────────────────────

function WaveLevel({ level }: { level: number }) {
  return (
    <div className="flex items-center gap-0.5 h-4">
      {Array.from({ length: 8 }).map((_, i) => {
        const threshold = (i + 1) / 8;
        const active    = level >= threshold;
        return (
          <span
            key={i}
            className={`w-0.5 rounded-full transition-all ${active ? 'bg-accent' : 'bg-midnight-4'}`}
            style={{ height: `${4 + i * 1.5}px` }}
          />
        );
      })}
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatDuration(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function preferredMimeType(): string {
  // Safari prefere mp4/m4a; Chrome/Firefox webm/opus
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg;codecs=opus',
  ];
  for (const c of candidates) {
    if (MediaRecorder.isTypeSupported(c)) return c;
  }
  return 'audio/webm';
}
